import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import useGetRequest from "hooks/useGetRequest";
import InflowOutflowCard from "./InflowOutflowCard";
import AccountDetailsDrawer from "./AccountDetailsDrawer";
import EmptyOutstandingPay from "../../asset/images/EmptyOutstandingPay.png";
import FundWalletModal from "./FundWalletModal";
import { useNavigate } from "react-router-dom";
import GenerateStatementModal from "./GenerateStatementModal";
import SuccessModal from "../SuccessModal";
import successIcon from "asset/icons/SuccessCurrencyConversion.svg";
import { fetchUserProfile } from "../../api/fetchers";
import PinModal from "../pay/PinModal";
import SelectFundingMethodModal from "./SelectFundingMethodModal";
import ReceiveViaWalletModal from "./ReceiveViaWalletModal";
import FundViaConversionModal from "./FundViaConversionModal";
import WalletBalanceCard from "./WalletBalanceCard";

const normalizeAccount = (raw, defaults, fallbackAccountName) => {
    const tryUpper = (v) => (typeof v === "string" ? v.toUpperCase() : v);

    const wallet = raw?.data?.data ?? raw?.data ?? raw ?? null;

    if (!wallet) return null;

    const walletCurrency = tryUpper(wallet.currency);
    const want = tryUpper(defaults);
    if (!walletCurrency || walletCurrency !== want) {
        return null;
    }

    const providers = Array.isArray(wallet.providers) ? wallet.providers : [];
    const provider =
        providers.find((p) => tryUpper(p?.currency) === walletCurrency) ?? providers[0] ?? {};

    return {
        currency: walletCurrency,
        balance: Number(wallet.balance ?? 0),
        accountNumber: provider.accountNumber || "",
        bankName: provider.bankName || provider.bank || "",
        branchName: provider.branchName || "",
        accountName: provider.accountName || fallbackAccountName || "",
        iban: provider.iban || "",
        bankAddress: provider?.bankAddress || "",
        bankCode: provider?.bankCode,
        routingNumber: provider?.routingNumber,
        paymentRails: provider?.paymentRails,
        swift: provider.swift || "",
        _raw: { wallet, provider },
    };
};

const OverviewSection = () => {
    const [userProfile, setUserProfile] = useState(null);
    const [showFundModal, setShowFundModal] = useState(false);
    const [showFundingMethodModal, setShowFundingMethodModal] = useState(false);
    const [showFundViaConversionModal, setShowFundViaConversionModal] = useState(false);
    const navigate = useNavigate();
    const {
        business,
        currency: defaults,
        businessSwitching,
        wallets,
    } = useSelector((state) => state);
    const hasSelectedWallet = (wallets || []).some(
        (wallet) =>
            String(wallet?.currency || "").toUpperCase() ===
            String(defaults || "").toUpperCase()
    );
    const [showDrawer, setShowDrawer] = useState(false);
    const [showGenerateModal, setShowGenerateModal] = useState(false);
    const [showReceiveWalletModal, setShowReceiveWalletModal] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [onPinSuccess, setOnPinSuccess] = useState(null);
    const [pinError, setPinError] = useState("");

    const isCryptoWallet = defaults === "USDT" || defaults === "USDC";
    const [fetchAccount, accountResponse, accountLoading] = useGetRequest();

    useEffect(() => {
        if (businessSwitching || !business?._id || !defaults || !hasSelectedWallet) return;
        const controller = new AbortController();
        fetchAccount(`/v1/wallets/account?currency=${encodeURIComponent(defaults)}`, {
            headers: { "x-business-id": business._id },
            signal: controller.signal,
        });
        return () => controller.abort();
    }, [business?._id, businessSwitching, defaults, fetchAccount, hasSelectedWallet]);

    const handleSelectTransfer = (type) => {
        if (!isBusinessApproved) return;

        if (isCryptoWallet) {
            const routeMap = {
                wallet: "/payment/send-crypto/crypto-wallet/amount",
                fiat: "/payment/send-crypto/fiat-wallet/amount",
            };
            navigate(routeMap[type] || "/payment/send-crypto");
        } else {
            if (type === "cross") {
                navigate("/payment/send-money/cross-border/bank-recipient/amount", {
                    state: {
                        default: defaults,
                        balance: balanceAmount,
                    },
                });
            } else {
                navigate("/payment/send-money", {
                    state: {
                        type,
                        default: defaults,
                        balance: balanceAmount,
                    },
                });
            }
        }
    };

    useEffect(() => {
        const getUserProfile = async () => {
            try {
                const userData = await fetchUserProfile();
                setUserProfile(userData);
            } catch (err) {
                console.log(err.message);
            }
        };
        getUserProfile();
    }, []);

    const isBusinessApproved = business?.status === "APPROVED";

    const account = normalizeAccount(accountResponse, defaults, business?.name);

    const balanceAmount = account?.balance ?? 0;
    const accountNumber = account?.accountNumber || "";
    const bankName = account?.bankName || "";
    const accountName = account?.accountName || business?.name || "";
    const branchName = account?.branchName;

    const handleAccountDetails = () => {
        setShowDrawer(true);
    };

    return (
        <>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
                {/* Wallet Balance Card */}
                <WalletBalanceCard
                    key={defaults}
                    businessId={business?._id}
                    defaults={defaults}
                    isBusinessApproved={isBusinessApproved}
                    accountLoading={accountLoading}
                    account={account}
                    onSelectTransfer={handleSelectTransfer}
                    onOpenFundModal={() => setShowFundModal(true)}
                    onOpenFundingMethodModal={() => setShowFundingMethodModal(true)}
                    onOpenAccountDetails={handleAccountDetails}
                    onOpenGenerateStatement={() => setShowGenerateModal(true)}
                />

                {/* Inflow & Outflow Card */}
                <InflowOutflowCard />

                {/* Outstanding Payments Card */}
                <div className="col-span-1 p-6 bg-white rounded-xl lg:col-span-2 xl:col-span-1">
                    <h2 className="text-xl font-medium text-start">Outstanding Payments</h2>

                    <div className="pt-4">
                        <img src={EmptyOutstandingPay} alt="Launching soon" className="w-32" />
                        <h3 className="text-2xl font-medium text-[#101828] mt-2">
                            Launching soon!
                        </h3>
                        <p className="text-sm text-[#6F6B7B] max-w-xs">
                            Soon you’ll manage all your pending payments right here.
                        </p>
                    </div>
                </div>
            </div>

            <AccountDetailsDrawer
                isOpen={showDrawer}
                onClose={() => setShowDrawer(false)}
                onAddNew={() => {}}
                account={{
                    currency: defaults,
                    accountName,
                    bankName,
                    accountNumber,
                    branchName,
                    paymentRails: account?.paymentRails || [],
                    iban: account?.iban || "",
                    swift: account?.swift || "",
                    bankAddress: account?.bankAddress || "",
                    bankCode: account?.bankCode,
                    routingNumber: account?.routingNumber,
                }}
            />

            <FundWalletModal
                isOpen={showFundModal}
                onClose={() => setShowFundModal(false)}
                walletData={{
                    currency: defaults,
                    accountName,
                    bankName,
                    accountNumber,
                    branchName,
                    iban: account?.iban || "",
                    swift: account?.swift || "",
                    bankAddress: account?.bankAddress || "",
                    bankCode: account?.bankCode,
                    routingNumber: account?.routingNumber,
                    paymentRails: account?.paymentRails || [],
                }}
                providers={account?._raw?.wallet?.providers}
            />

            <FundViaConversionModal
                isOpen={showFundViaConversionModal}
                onClose={() => setShowFundViaConversionModal(false)}
            />

            <GenerateStatementModal
                show={showGenerateModal}
                onClose={() => {
                    setShowGenerateModal(false);
                }}
                wallet={accountNumber}
                email={userProfile?.data?.email || userProfile?.email || ""}
                setShowSuccessModal={setShowSuccessModal}
                setGeneratedData={() => {}}
                setShowPinModal={setShowPinModal}
                setOnPinSuccess={setOnPinSuccess}
            />

            {showSuccessModal && (
                <SuccessModal
                    show={true}
                    onClose={() => setShowSuccessModal(false)}
                    icon={
                        <div className="p-1 rounded-full">
                            <img src={successIcon} alt="success" className="w-24 h-24" />
                        </div>
                    }
                    title="Your statement is ready!"
                    message="We’ve sent a copy to your email."
                    buttonText="Done"
                    onButtonClick={() => {
                        setShowSuccessModal(false);
                    }}
                />
            )}

            <SelectFundingMethodModal
                isOpen={showFundingMethodModal}
                onClose={() => setShowFundingMethodModal(false)}
                onWalletOptionSelect={() => {
                    setShowFundingMethodModal(false);
                    setShowReceiveWalletModal(true);
                }}
                onConversionOptionSelect={() => {
                    setShowFundingMethodModal(false);
                    setShowFundViaConversionModal(true);
                }}
            />

            <ReceiveViaWalletModal
                isOpen={showReceiveWalletModal}
                onClose={() => setShowReceiveWalletModal(false)}
                walletAddress={accountNumber}
            />

            <PinModal
                isOpen={showPinModal}
                onClose={() => {
                    setShowPinModal(false);
                    setPinError("");
                }}
                onConfirm={async (enteredPin) => {
                    setPinError("");
                    if (onPinSuccess) {
                        try {
                            await onPinSuccess(enteredPin);
                        } catch (err) {
                            setPinError(
                                err?.response?.data?.message || "Incorrect PIN. Please try again."
                            );
                            throw err;
                        }
                    }
                }}
                header="Enter your PIN to view statement"
                pinErrorMessage={pinError}
            />
        </>
    );
};

export default OverviewSection;
